import React from 'react';
import ReactDOM from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/code-highlight/styles.css';
import '@xyflow/react/dist/style.css';

import { App } from './App';
import { theme } from './theme/theme';
import { ApiRequestError, restoreSession } from './api/client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 is handled by the client's transparent refresh; retrying a 4xx
      // here would just repeat a request the server already answered
      // definitively, three times, while the user waits.
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Attempt a refresh BEFORE the first render, so a reload with a live cookie
// lands on the dashboard instead of flashing the login screen and bouncing back.
restoreSession().finally(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MantineProvider theme={theme} defaultColorScheme="auto">
        <QueryClientProvider client={queryClient}>
          <ModalsProvider>
            <Notifications position="top-right" />
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ModalsProvider>
        </QueryClientProvider>
      </MantineProvider>
    </React.StrictMode>,
  );
});
